import { type FC, useCallback, useId, useRef } from 'react'
import { FormattedMessage } from 'react-intl'

import { IconClose, IconSearch } from '@redteclab/icons'

import { FormControl, FormLabel, Input } from 'base-ui'

import { useRunCallbackWhenNavigated } from '../../../../../../navigation-listener'
import { useHeaderSearchContext } from '../../../headerSearchContext'
import { useHeaderSearchPlaceholderText } from '../../../useHeaderSearchPlaceholderText'
import { handleKeyPressedOnSearchInput } from '../../HeaderSearchActiveSearchBox/HeaderSearchAlgoliaContainer/HeaderSearchResultsContainer/HeaderSearchResults/handleKeyPressedOnSearchInput'

export const HeaderSearchDesktopInput: FC = () => {
  const headerSearchContext = useHeaderSearchContext()
  const { handleSubmitSearch, inputControls } = headerSearchContext
  const headerSearchPlaceholderText = useHeaderSearchPlaceholderText()
  const resultsPortal = useRef<HTMLDivElement | null>(null)
  const inputId = useId()

  useRunCallbackWhenNavigated(
    useCallback(() => {
      headerSearchContext.setSearchIsActive({
        value: false,
      })
    }, [headerSearchContext]),
  )

  return (
    <>
      <form
        className="relative top-1/2 mb-4 flex -translate-y-1/2 flex-row"
        onSubmit={(event): void => {
          /**
           * We do "submitting" via js.
           * Form is left as fallback, for cases when javascript will fail to hydrate for some reason,
           * but user still be able to land on search results page.
           */
          event.preventDefault()
          handleSubmitSearch()
        }}
      >
        <FormControl
          className="w-full"
          onChange={inputControls.handleInputChange}
          value={inputControls.searchInputValue}
        >
          <FormLabel htmlFor={inputId}>{headerSearchPlaceholderText}</FormLabel>
          <Input
            endAdornment={
              inputControls.searchInputValue === '' ? null : (
                <button
                  onClick={() => {
                    inputControls.clearSearch()
                  }}
                  type="button"
                >
                  <IconClose className="-mr-2 size-8 text-dark-brand" />
                </button>
              )
            }
            name="query"
            onFocus={(): void => {
              headerSearchContext.setSearchIsActive({
                portalResultsToElement: resultsPortal.current ?? undefined,
                value: true,
              })
            }}
            onKeyUp={(event): void => {
              handleKeyPressedOnSearchInput(event, headerSearchContext)
            }}
            slotProps={{
              input: {
                className:
                  'w-full text-s placeholder:text-dark-primary-strong placeholder:font-normal outline-none bg-transparent',
                'data-qa-id': 'search-box-query',
                id: inputId,
              },
              root: {
                className:
                  'flex items-center rounded-l-full rounded-r-none border-y border-l border-dark-accent bg-light-primary-medium hover:border-dark-brand transition-colors',
              },
            }}
            type="text"
          />
        </FormControl>
        <button
          className="flex items-center whitespace-nowrap rounded-r-full bg-dark-accent px-3.5 text-m text-light-primary-strong hover:bg-dark-pressed-redpoints"
          data-qa-id="search-box-query-button"
          type="submit"
        >
          <IconSearch className="mr-2.5 size-8 fill-light-primary-strong" />

          <span className="sr-only font-medium desktop:not-sr-only">
            <FormattedMessage id="general.button.label.search" />
          </span>
        </button>
      </form>
      <div ref={resultsPortal} />
    </>
  )
}
